// MoveNet keypoint names and indices
export const KEYPOINT_DICT = {
  'nose': 0,
  'left_eye': 1,
  'right_eye': 2,
  'left_ear': 3,
  'right_ear': 4,
  'left_shoulder': 5,
  'right_shoulder': 6,
  'left_elbow': 7,
  'right_elbow': 8,
  'left_wrist': 9,
  'right_wrist': 10,
  'left_hip': 11,
  'right_hip': 12,
  'left_knee': 13,
  'right_knee': 14,
  'left_ankle': 15,
  'right_ankle': 16
};

// Skeleton connections for drawing
export const SKELETON_CONNECTIONS = [
  ['left_shoulder', 'right_shoulder'],
  ['left_shoulder', 'left_elbow'],
  ['left_elbow', 'left_wrist'],
  ['right_shoulder', 'right_elbow'],
  ['right_elbow', 'right_wrist'],
  ['left_shoulder', 'left_hip'],
  ['right_shoulder', 'right_hip'],
  ['left_hip', 'right_hip'],
  ['left_hip', 'left_knee'],
  ['left_knee', 'left_ankle'],
  ['right_hip', 'right_knee'],
  ['right_knee', 'right_ankle']
];

const CONFIDENCE_THRESHOLD = 0.3;

/**
 * Draw keypoints on canvas
 */
export function drawKeypoints(keypoints, ctx) {
  keypoints.forEach((keypoint) => {
    if (keypoint.score > CONFIDENCE_THRESHOLD) {
      const { x, y } = keypoint;

      ctx.beginPath();
      ctx.arc(x, y, 5, 0, 2 * Math.PI);
      ctx.fillStyle = '#00ff00';
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.stroke();
    }
  });
}

/**
 * Draw skeleton connections on canvas
 */
export function drawSkeleton(keypoints, ctx) {
  const keypointMap = {};
  keypoints.forEach((keypoint, index) => {
    const name = Object.keys(KEYPOINT_DICT).find(key => KEYPOINT_DICT[key] === index);
    if (name) {
      keypointMap[name] = keypoint;
    }
  });

  ctx.strokeStyle = '#00ff00';
  ctx.lineWidth = 3;

  SKELETON_CONNECTIONS.forEach(([startName, endName]) => {
    const start = keypointMap[startName];
    const end = keypointMap[endName];

    if (start && end &&
        start.score > CONFIDENCE_THRESHOLD &&
        end.score > CONFIDENCE_THRESHOLD) {
      ctx.beginPath();
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(end.x, end.y);
      ctx.stroke();
    }
  });
}

/**
 * Get keypoint by name
 */
export function getKeypoint(keypoints, name) {
  const index = KEYPOINT_DICT[name];
  if (index !== undefined && keypoints[index]) {
    return keypoints[index];
  }
  return null;
}

/**
 * Calculate angle between three points
 */
export function calculateAngle(pointA, pointB, pointC) {
  const radians = Math.atan2(pointC.y - pointB.y, pointC.x - pointB.x) -
                  Math.atan2(pointA.y - pointB.y, pointA.x - pointB.x);
  let angle = Math.abs(radians * 180 / Math.PI);

  if (angle > 180) {
    angle = 360 - angle;
  }

  return angle;
}

/**
 * Calculate distance between two points
 */
export function calculateDistance(pointA, pointB) {
  return Math.sqrt(
    Math.pow(pointB.x - pointA.x, 2) +
    Math.pow(pointB.y - pointA.y, 2)
  );
}

/**
 * Draw ground level line for jump detection
 */
export function drawGroundLine(groundY, canvasWidth, ctx) {
  if (!groundY) return;

  ctx.beginPath();
  ctx.moveTo(0, groundY);
  ctx.lineTo(canvasWidth, groundY);
  ctx.strokeStyle = '#ffff00'; // Yellow
  ctx.lineWidth = 2;
  ctx.setLineDash([10, 5]); // Dashed line
  ctx.stroke();
  ctx.setLineDash([]); // Reset to solid

  // Add label
  ctx.fillStyle = '#ffff00';
  ctx.font = 'bold 14px Arial';
  ctx.fillText('Ground Level', 10, groundY - 10);
}

/**
 * Draw ankle positions with height indicators
 */
export function drawAnkleHeights(keypoints, groundY, ctx) {
  const leftAnkle = getKeypoint(keypoints, 'left_ankle');
  const rightAnkle = getKeypoint(keypoints, 'right_ankle');

  if (!leftAnkle || !rightAnkle || !groundY) return;

  const avgAnkleY = (leftAnkle.y + rightAnkle.y) / 2;
  const heightAboveGround = groundY - avgAnkleY;

  // Draw vertical line from ankles to ground
  if (leftAnkle.score > CONFIDENCE_THRESHOLD) {
    ctx.beginPath();
    ctx.moveTo(leftAnkle.x, leftAnkle.y);
    ctx.lineTo(leftAnkle.x, groundY);
    ctx.strokeStyle = heightAboveGround > 15 ? '#ff00ff' : '#00ffff'; // Magenta if skipping (>15px), cyan if not
    ctx.lineWidth = 2;
    ctx.stroke();

    // Draw ankle Y coordinate label
    ctx.fillStyle = '#ffffff';
    ctx.font = '10px monospace';
    ctx.fillText(`L: ${leftAnkle.y.toFixed(1)}`, leftAnkle.x + 8, leftAnkle.y);
  }

  if (rightAnkle.score > CONFIDENCE_THRESHOLD) {
    ctx.beginPath();
    ctx.moveTo(rightAnkle.x, rightAnkle.y);
    ctx.lineTo(rightAnkle.x, groundY);
    ctx.strokeStyle = heightAboveGround > 15 ? '#ff00ff' : '#00ffff';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Draw ankle Y coordinate label
    ctx.fillStyle = '#ffffff';
    ctx.font = '10px monospace';
    ctx.fillText(`R: ${rightAnkle.y.toFixed(1)}`, rightAnkle.x + 8, rightAnkle.y + 10);
  }

  // Draw detailed height measurements with exact coordinates
  const isSkipping = heightAboveGround > 15;
  ctx.fillStyle = isSkipping ? '#ff00ff' : '#00ffff';
  ctx.font = 'bold 14px monospace';
  const y = 20;
  ctx.fillText(`Left Ankle Y:  ${leftAnkle.y.toFixed(1)}px`, 10, y);
  ctx.fillText(`Right Ankle Y: ${rightAnkle.y.toFixed(1)}px`, 10, y + 16);
  ctx.fillText(`Avg Ankle Y:   ${avgAnkleY.toFixed(1)}px`, 10, y + 32);
  ctx.fillText(`Ground Y:      ${groundY.toFixed(1)}px`, 10, y + 48);
  ctx.fillText(`Height:        ${heightAboveGround.toFixed(1)}px ${isSkipping ? '<- SKIP!' : ''}`, 10, y + 64);
}

/**
 * Draw status overlay
 */
export function drawStatusOverlay(state, ctx) {
  if (!state) return;

  const { isCalibrating, isInAir, repCount } = state;

  // Draw calibration status (top left)
  if (isCalibrating) {
    ctx.fillStyle = 'rgba(255, 165, 0, 0.8)'; // Orange background
    ctx.fillRect(10, 50, 250, 40);
    ctx.fillStyle = '#000000';
    ctx.font = 'bold 18px Arial';
    ctx.fillText('Calibrating... Stand still!', 20, 75);
  }

  // Draw jump state (bottom right corner)
  if (!isCalibrating && isInAir) {
    const boxWidth = 150;
    const boxHeight = 40;
    // Use actual canvas dimensions instead of hardcoded values
    const canvasWidth = ctx.canvas.width;
    const canvasHeight = ctx.canvas.height;
    const x = canvasWidth - boxWidth - 10;
    const y = canvasHeight - boxHeight - 10;

    ctx.fillStyle = 'rgba(255, 0, 255, 0.8)'; // Magenta background
    ctx.fillRect(x, y, boxWidth, boxHeight);
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 20px Arial';
    ctx.fillText('🚀 JUMPING!', x + 10, y + 27);
  }
}

/**
 * Draw bounding box around detected person
 */
export function drawBoundingBox(keypoints, ctx) {
  // Filter keypoints with good confidence
  const validKeypoints = keypoints.filter(kp => kp.score > CONFIDENCE_THRESHOLD);

  if (validKeypoints.length === 0) return;

  // Find bounding box coordinates
  const xs = validKeypoints.map(kp => kp.x);
  const ys = validKeypoints.map(kp => kp.y);

  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  const width = maxX - minX;
  const height = maxY - minY;

  // Add some padding
  const padding = 20;
  const x = minX - padding;
  const y = minY - padding;
  const w = width + (padding * 2);
  const h = height + (padding * 2);

  // Draw colored corners (like in the reference image)
  const cornerLength = 40;
  const lineWidth = 4;

  ctx.lineWidth = lineWidth;

  // Top-left corner (red)
  ctx.strokeStyle = '#ff0000';
  ctx.beginPath();
  ctx.moveTo(x, y + cornerLength);
  ctx.lineTo(x, y);
  ctx.lineTo(x + cornerLength, y);
  ctx.stroke();

  // Top-right corner (green)
  ctx.strokeStyle = '#00ff00';
  ctx.beginPath();
  ctx.moveTo(x + w - cornerLength, y);
  ctx.lineTo(x + w, y);
  ctx.lineTo(x + w, y + cornerLength);
  ctx.stroke();

  // Bottom-left corner (red)
  ctx.strokeStyle = '#ff0000';
  ctx.beginPath();
  ctx.moveTo(x, y + h - cornerLength);
  ctx.lineTo(x, y + h);
  ctx.lineTo(x + cornerLength, y + h);
  ctx.stroke();

  // Bottom-right corner (blue)
  ctx.strokeStyle = '#0000ff';
  ctx.beginPath();
  ctx.moveTo(x + w - cornerLength, y + h);
  ctx.lineTo(x + w, y + h);
  ctx.lineTo(x + w, y + h - cornerLength);
  ctx.stroke();

  // Draw connecting lines between corners (lighter color)
  ctx.strokeStyle = 'rgba(0, 255, 0, 0.5)';
  ctx.lineWidth = 2;
  ctx.strokeRect(x, y, w, h);
}
